import React, { useState, useEffect } from 'react';
import { Form, message, Input, Button, Tag, Divider } from 'antd';
import Card, { CardDelete, CardEdit } from 'components/Card/Card';
import { useEditCard } from 'components/Card/useEditCard';
import TabularList from 'components/TabularList/TabularList';
import { getFormattedPhoneNumber, getPhoneNumberValidator, getPhoneEXTValidator } from 'utils';
import dayjs from 'dayjs';
import { DATE_FORMAT_US_SLASH } from 'components/AriaDatePicker/AriaDatePicker';
import { ENDASH, PHONE_NUMBER_EXT_CHARLIMIT } from 'constants';
import providerService from 'services/providers.service';
import commonService from 'services/common.service';
import Switch from 'components/Switch/Switch';
import RadioCards from 'components/RadioCards/RadioCards';
import { DatePicker } from 'components/ui/DatePicker';
import { Add } from '@carbon/icons-react';
import PhoneNumberContainer from 'components/PhoneNumberContainer';
import PhoneNumber from 'components/PhoneNumber';
import classNames from 'classnames';

const StaffMemberCard = ({
  staff,
  staffCount,
  idx,
  isEditing,
  form,
  certificateTypes,
  onDeleteStaff,
  onAddCertification,
  onDeleteCertification,
}) => {
  const presentOnWeekends = Form.useWatch(`presentOnWeekends_${idx}`, form);

  return (
    <Card
      className={classNames({
        'mt-4': idx !== 0,
        'mb-4': idx !== staffCount - 1,
      })}
      collapsible
      title={`${staff.person?.firstName || ''} ${staff.person?.lastName || ''}`.trim() || `Staff member ${idx + 1}`}
      subtitle={idx === 0 ? '(license holder)' : staff.person?.position}
      extra={isEditing ? <CardDelete onConfirm={() => onDeleteStaff(idx)} /> : null}
      noBodyPadding
    >
      <TabularList
        indented
        data={[
          {
            title: 'First name',
            value: isEditing ? (
              <Form.Item
                name={`firstName_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'First name is required' }]}
              >
                <Input className="max-w-72" />
              </Form.Item>
            ) : (
              staff.person?.firstName
            ),
            titleRequired: true,
          },
          {
            title: 'Last name',
            value: isEditing ? (
              <Form.Item
                name={`lastName_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'Last name is required' }]}
              >
                <Input className="max-w-72" />
              </Form.Item>
            ) : (
              staff.person?.lastName
            ),
            titleRequired: true,
          },
          {
            title: 'Date of birth',
            value: isEditing ? (
              <Form.Item
                name={`dateOfBirth_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'Date of birth is required' }]}
              >
                <DatePicker className="max-w-72" />
              </Form.Item>
            ) : (
              dayjs(staff.person?.dateOfBirth).format(DATE_FORMAT_US_SLASH)
            ),
            titleRequired: true,
          },
          {
            title: 'Role',
            value: isEditing ? (
              <Form.Item
                name={`position_${idx}`}
                className="mb-0"
                rules={[{ required: true, message: 'Role is required' }]}
              >
                <Input className="max-w-72" />
              </Form.Item>
            ) : (
              staff.person?.position
            ),
            titleRequired: true,
          },
          {
            title: 'Present on weekends?',
            value: isEditing ? (
              <Form.Item
                name={`presentOnWeekends_${idx}`}
                className="mb-0"
                valuePropName="checked"
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>
            ) : staff.presentOnWeekends ? (
              'Yes'
            ) : (
              'No'
            ),
            titleRequired: true,
          },
        ]}
      />

      {((staff.certifications?.length > 0 && !isEditing) || (isEditing && presentOnWeekends)) && (
        <>
          <Divider className="mt-0 mb-4" />
          <div className="px-8 pb-6">
            <div className="flex items-center gap-3 mb-4">
              <h4 className="text-base font-semibold">Certifications</h4>
              <Tag>{staff.certifications?.length || 0}</Tag>
            </div>

            {staff.certifications?.map((cert, certIdx) => (
              <Card
                key={cert?.id || `new-cert-${certIdx}`}
                className="mb-3"
                collapsible
                title={cert.certificationType?.description || `Certification ${certIdx + 1}`}
                subtitle={cert.number ? `#${cert.number}` : ''}
                extra={isEditing ? <CardDelete onConfirm={() => onDeleteCertification(idx, certIdx)} /> : null}
                noBodyPadding
              >
                <TabularList
                  indented
                  data={[
                    {
                      title: 'Certification type',
                      value: isEditing ? (
                        <Form.Item
                          name={`certificationType_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[{ required: true, message: 'Certification type is required' }]}
                        >
                          <RadioCards
                            size="small"
                            options={certificateTypes?.map((type) => ({
                              value: type.id,
                              label: type.title,
                            }))}
                          />
                        </Form.Item>
                      ) : (
                        cert.certificationType?.description || ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Certificate number',
                      value: isEditing ? (
                        <Form.Item
                          name={`certNumber_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[{ required: true, message: 'Certificate number is required' }]}
                        >
                          <Input className="max-w-72" />
                        </Form.Item>
                      ) : (
                        cert.number || ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Certificate issue date',
                      value: isEditing ? (
                        <Form.Item
                          name={`issueDate_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[{ required: true, message: 'Issue date is required' }]}
                        >
                          <DatePicker className="max-w-72" />
                        </Form.Item>
                      ) : cert.issueDate ? (
                        dayjs(cert.issueDate).format(DATE_FORMAT_US_SLASH)
                      ) : (
                        ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Certificate expiration date',
                      value: isEditing ? (
                        <Form.Item
                          name={`expirationDate_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[{ required: true, message: 'Expiration date is required' }]}
                        >
                          <DatePicker className="max-w-72" />
                        </Form.Item>
                      ) : cert.expirationDate ? (
                        dayjs(cert.expirationDate).format(DATE_FORMAT_US_SLASH)
                      ) : (
                        ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Instructor first name',
                      value: isEditing ? (
                        <Form.Item
                          name={`instructorFirstName_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[{ required: true, message: 'Instructor first name is required' }]}
                        >
                          <Input className="max-w-72" />
                        </Form.Item>
                      ) : (
                        cert.instructor?.firstName || ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Instructor last name',
                      value: isEditing ? (
                        <Form.Item
                          name={`instructorLastName_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[{ required: true, message: 'Instructor last name is required' }]}
                        >
                          <Input className="max-w-72" />
                        </Form.Item>
                      ) : (
                        cert.instructor?.lastName || ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Instructor email',
                      value: isEditing ? (
                        <Form.Item
                          name={`instructorEmail_${idx}_${certIdx}`}
                          className="mb-0"
                          rules={[
                            { required: true, message: 'Instructor email is required' },
                            { type: 'email', message: 'Invalid email address' },
                          ]}
                        >
                          <Input className="max-w-72" />
                        </Form.Item>
                      ) : (
                        cert.instructor?.email || ENDASH
                      ),
                      titleRequired: true,
                    },
                    {
                      title: 'Instructor phone',
                      value: isEditing ? (
                        <PhoneNumberContainer
                          phoneNumber={
                            <Form.Item
                              name={`instructorPhone_${idx}_${certIdx}`}
                              className="mb-0"
                              rules={[
                                { required: true, message: 'Instructor phone number is required' },
                                getPhoneNumberValidator('Invalid Phone Number'),
                              ]}
                            >
                              <PhoneNumber />
                            </Form.Item>
                          }
                          extension={
                            <Form.Item
                              name={`instructorPhoneExt_${idx}_${certIdx}`}
                              className="mb-0"
                              rules={[getPhoneEXTValidator('Invalid Extension')]}
                            >
                              <Input maxLength={PHONE_NUMBER_EXT_CHARLIMIT} placeholder="Ext." />
                            </Form.Item>
                          }
                        />
                      ) : (
                        <>
                          {getFormattedPhoneNumber(cert.instructor?.phone) || ENDASH}
                          {cert.instructor?.phoneExt && ` ext. ${cert.instructor.phoneExt}`}
                        </>
                      ),
                      titleRequired: true,
                    },
                  ]}
                />
              </Card>
            ))}

            {isEditing && presentOnWeekends && (
              <Button
                color="default"
                variant="dashed"
                onClick={() => onAddCertification(idx)}
                icon={<Add size={20} />}
                block
                className="mt-3"
              >
                Add certification
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
};

const StaffMembers = ({ application, applicationFiles, isEditable = false }) => {
  const [localStaff, setLocalStaff] = useState(() => JSON.parse(JSON.stringify(application?.staff || [])));
  const [deletedStaff, setDeletedStaff] = useState([]);
  const [deletedCertifications, setDeletedCertifications] = useState([]);
  const [certificateTypes, setCertificateTypes] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    const fetchCertificateTypes = async () => {
      try {
        const types = await commonService.get('/providers/options/41');
        setCertificateTypes(types);
      } catch (error) {
        console.error('Error loading certificate types:', error);
      }
    };
    fetchCertificateTypes();
  }, []);

  const { isEditing, editProps } = useEditCard({
    id: 'staff-members',
    onEditStart: () => {
      const formValues = {};
      localStaff.forEach((staff, idx) => {
        formValues[`firstName_${idx}`] = staff.person?.firstName;
        formValues[`lastName_${idx}`] = staff.person?.lastName;
        formValues[`dateOfBirth_${idx}`] = staff.person?.dateOfBirth
          ? dayjs(staff.person.dateOfBirth).format('YYYY-MM-DD')
          : '';
        formValues[`position_${idx}`] = staff.person?.position;
        formValues[`presentOnWeekends_${idx}`] = staff.presentOnWeekends;
        staff.certifications?.forEach((cert, certIdx) => {
          formValues[`certificationType_${idx}_${certIdx}`] = cert.certificationType?.id;
          formValues[`certNumber_${idx}_${certIdx}`] = cert.number;
          formValues[`issueDate_${idx}_${certIdx}`] = cert.issueDate ? dayjs(cert.issueDate).format('YYYY-MM-DD') : '';
          formValues[`expirationDate_${idx}_${certIdx}`] = cert.expirationDate
            ? dayjs(cert.expirationDate).format('YYYY-MM-DD')
            : '';
          formValues[`instructorFirstName_${idx}_${certIdx}`] = cert.instructor?.firstName;
          formValues[`instructorLastName_${idx}_${certIdx}`] = cert.instructor?.lastName;
          formValues[`instructorEmail_${idx}_${certIdx}`] = cert.instructor?.email;
          formValues[`instructorPhone_${idx}_${certIdx}`] = cert.instructor?.phone;
          formValues[`instructorPhoneExt_${idx}_${certIdx}`] = cert.instructor?.phoneExt;
        });
      });
      form.setFieldsValue(formValues);
    },
    onSave: async () => {
      await form.validateFields();
      const values = form.getFieldsValue();
      const deleteCertPromises = deletedCertifications.map(async ({ staffId, certId }) => {
        if (certId) return providerService.deleteStaffMemberCert(application.id, staffId, certId);
      });
      const deleteStaffPromises = deletedStaff.map(async (staff) => {
        if (staff.id) {
          if (staff.certifications) {
            for (const cert of staff.certifications) {
              if (cert.id) await providerService.deleteStaffMemberCert(application.id, staff.id, cert.id);
            }
          }
          return providerService.deleteStaffMember(application.id, staff.id);
        }
      });
      await Promise.all([...deleteCertPromises, ...deleteStaffPromises]);
      for (let idx = 0; idx < localStaff.length; idx++) {
        const staff = localStaff[idx];
        const staffPayload = {
          ...(staff.id ? { id: staff.id } : {}),
          person: {
            firstName: values[`firstName_${idx}`],
            lastName: values[`lastName_${idx}`],
            dateOfBirth: new Date(values[`dateOfBirth_${idx}`]).toISOString(),
            position: values[`position_${idx}`],
          },
          presentOnWeekends: values[`presentOnWeekends_${idx}`],
          application: { id: application.id },
        };
        const updatedStaffMember = await providerService.addUpdateStaffMembers(application.id, staffPayload);
        if (staff.certifications) {
          for (let certIdx = 0; certIdx < staff.certifications.length; certIdx++) {
            const cert = staff.certifications[certIdx];
            const certPayload = {
              ...(cert.id ? { id: cert.id } : {}),
              certificationType: { id: values[`certificationType_${idx}_${certIdx}`] },
              number: values[`certNumber_${idx}_${certIdx}`],
              issueDate: new Date(values[`issueDate_${idx}_${certIdx}`]).toISOString(),
              expirationDate: new Date(values[`expirationDate_${idx}_${certIdx}`]).toISOString(),
              instructor: {
                firstName: values[`instructorFirstName_${idx}_${certIdx}`],
                lastName: values[`instructorLastName_${idx}_${certIdx}`],
                email: values[`instructorEmail_${idx}_${certIdx}`],
                phone: values[`instructorPhone_${idx}_${certIdx}`],
                phoneExt: values[`instructorPhoneExt_${idx}_${certIdx}`],
              },
            };
            await providerService.addUpdateStaffMemberCert(application.id, updatedStaffMember.id, certPayload);
          }
        }
      }
      const refreshedStaff = await providerService.getStaffMembers(application.id);
      setLocalStaff(refreshedStaff || []);
      setDeletedStaff([]);
      setDeletedCertifications([]);
      form.resetFields();
      message.success('Staff members updated successfully');
    },
    onDiscard: () => {
      setLocalStaff(JSON.parse(JSON.stringify(application?.staff || [])));
      setDeletedStaff([]);
      setDeletedCertifications([]);
      form.resetFields();
    },
  });

  const handleDeleteStaff = (staffIndex) => {
    const staff = localStaff[staffIndex];
    if (staff.id) {
      setDeletedStaff([...deletedStaff, staff]);
    }
    const updatedStaff = localStaff.filter((_, idx) => idx !== staffIndex);
    setLocalStaff(updatedStaff);
  };

  const handleAddStaff = () => {
    const newStaff = {
      id: null,
      person: {
        firstName: '',
        lastName: '',
        dateOfBirth: new Date().toISOString(),
        position: '',
      },
      presentOnWeekends: false,
      certifications: [],
    };

    const updatedStaff = [...localStaff, newStaff];
    setLocalStaff(updatedStaff);

    const idx = updatedStaff.length - 1;
    form.setFieldsValue({
      [`firstName_${idx}`]: '',
      [`lastName_${idx}`]: '',
      [`dateOfBirth_${idx}`]: dayjs().format('YYYY-MM-DD'),
      [`position_${idx}`]: '',
      [`presentOnWeekends_${idx}`]: false,
    });
  };

  const handleAddCertification = (staffIndex) => {
    const updatedStaff = [...localStaff];
    if (!updatedStaff[staffIndex].certifications) {
      updatedStaff[staffIndex].certifications = [];
    }
    updatedStaff[staffIndex].certifications.push({
      id: null,
      certificationType: { id: null },
      number: '',
      issueDate: new Date().toISOString(),
      expirationDate: new Date().toISOString(),
      instructor: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        phoneExt: '',
      },
    });
    setLocalStaff(updatedStaff);

    const certIdx = updatedStaff[staffIndex].certifications.length - 1;
    form.setFieldsValue({
      [`certificationType_${staffIndex}_${certIdx}`]: null,
      [`certNumber_${staffIndex}_${certIdx}`]: '',
      [`issueDate_${staffIndex}_${certIdx}`]: dayjs().format('YYYY-MM-DD'),
      [`expirationDate_${staffIndex}_${certIdx}`]: dayjs().format('YYYY-MM-DD'),
      [`instructorFirstName_${staffIndex}_${certIdx}`]: '',
      [`instructorLastName_${staffIndex}_${certIdx}`]: '',
      [`instructorEmail_${staffIndex}_${certIdx}`]: '',
      [`instructorPhone_${staffIndex}_${certIdx}`]: '',
      [`instructorPhoneExt_${staffIndex}_${certIdx}`]: '',
    });
  };

  const handleDeleteCertification = (staffIndex, certIndex) => {
    const updatedStaff = localStaff.map((staff, idx) => {
      if (idx === staffIndex) {
        const cert = staff.certifications[certIndex];

        // Track deleted certification if it has an ID (existing certification)
        if (cert.id && staff.id) {
          setDeletedCertifications([...deletedCertifications, { staffId: staff.id, certId: cert.id }]);
        }

        // Return new staff object with filtered certifications
        return {
          ...staff,
          certifications: staff.certifications.filter((_, certIdx) => certIdx !== certIndex),
        };
      }
      return staff;
    });

    setLocalStaff(updatedStaff);
  };

  return (
    <Card
      title={
        <div className="flex items-center gap-3">
          <span>Staff members</span>
          <Tag>{localStaff.length}</Tag>
        </div>
      }
      extra={isEditable ? <CardEdit {...editProps} /> : null}
      stickyHeader
    >
      <Form form={form} disabled={editProps.loading}>
        {localStaff.length > 0 &&
          localStaff.map((staff, idx) => (
            <StaffMemberCard
              key={staff?.id || `new-${idx}`}
              staff={staff}
              staffCount={localStaff.length || 0}
              idx={idx}
              isEditing={isEditing}
              form={form}
              certificateTypes={certificateTypes}
              onDeleteStaff={handleDeleteStaff}
              onAddCertification={handleAddCertification}
              onDeleteCertification={handleDeleteCertification}
            />
          ))}
      </Form>

      {isEditing && isEditable && (
        <Button
          color="default"
          variant="dashed"
          onClick={handleAddStaff}
          icon={<Add size={20} />}
          block
          className="mt-4"
          disabled={editProps.loading}
        >
          Add staff member
        </Button>
      )}
    </Card>
  );
};

export default StaffMembers;
